import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../Pages/CSS/Dashboard.css";
import { backend_url, currency } from "../App";
import { canAccess } from "../utils/menuAccess";

// App.js loads this page, so read `currency` when formatting, not at load time
// (reading it at the top of the file crashes with "before initialization").
const money = (n) => `${currency || "₹"}${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const dayKey = (d) => { const x = new Date(d); return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`; };
const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  if (s < 172800) return "yesterday";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Order flow, in order, with the colours used everywhere on this page
export const STATUSES = [
  { key: "Pending",   label: "New",        color: "#f59e0b", hint: "Waiting to be confirmed" },
  { key: "Confirmed", label: "Confirmed",  color: "#6366f1", hint: "Ready to pack & ship" },
  { key: "Shipped",   label: "Shipped",    color: "#0ea5e9", hint: "On the way" },
  { key: "Delivered", label: "Delivered",  color: "#16a34a", hint: "Reached the customer" },
  { key: "Cancelled", label: "Cancelled",  color: "#ef4444", hint: "Cancelled orders" },
];
const statusOf = (o) => (o.status === "Completed" ? "Delivered" : o.status || "Pending");
const counts = (o) => statusOf(o) !== "Cancelled" && o.refundStatus !== "Refunded"; // orders that count as sales
const stockOf = (p) => {
  const s = String(p.status || "").toLowerCase().replace(/[^a-z]/g, "");
  if (s === "outofstock") return "out";
  if (s === "lowstock") return "low";
  return "in";
};

// All dashboard numbers from the raw data (kept separate so it can be tested)
export const computeDashboard = ({ orders = [], products = [], reviews = [], now = new Date() }) => {
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const today = dayKey(now);

  const sales = orders.filter(counts);
  const sum = (list) => list.reduce((t, o) => t + (Number(o.totalPrice) || 0), 0);
  const thisMonth = sales.filter((o) => new Date(o.orderDate) >= startOfMonth);
  const lastMonth = sales.filter((o) => { const d = new Date(o.orderDate); return d >= startOfLastMonth && d < startOfMonth; });
  const monthSales = sum(thisMonth), lastMonthSales = sum(lastMonth);

  const byStatus = Object.fromEntries(STATUSES.map((s) => [s.key, 0]));
  orders.forEach((o) => { byStatus[statusOf(o)] = (byStatus[statusOf(o)] || 0) + 1; });

  // daily sales for the chart (last 30 days, oldest first)
  const daily = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    daily.push({ date: d, key: dayKey(d), total: 0, orders: 0 });
  }
  const dayIndex = Object.fromEntries(daily.map((d, i) => [d.key, i]));
  sales.forEach((o) => {
    const i = dayIndex[dayKey(o.orderDate)];
    if (i !== undefined) { daily[i].total += Number(o.totalPrice) || 0; daily[i].orders += 1; }
  });

  // stock health of live (not archived) products
  const live = products.filter((p) => !p.isArchived);
  const stock = { in: 0, low: 0, out: 0 };
  live.forEach((p) => { stock[stockOf(p)] += 1; });
  // a few products to restock first: out of stock before low, newest first
  const restock = live
    .filter((p) => stockOf(p) !== "in")
    .sort((a, b) => (stockOf(a) === "out" ? 0 : 1) - (stockOf(b) === "out" ? 0 : 1) || (b.id || 0) - (a.id || 0))
    .slice(0, 4)
    .map((p) => ({ id: p._id, name: p.name, code: p.unique_id, image: p.images?.[0], level: stockOf(p) }));

  // most ordered (by pieces sold) with product photo
  const sold = {};
  sales.forEach((o) => {
    const p = o.productId;
    if (!p) return;
    const id = p._id || p;
    sold[id] = sold[id] || { id, name: p.name || "Product", image: p.images?.[0], code: p.unique_id, qty: 0, orders: 0 };
    sold[id].qty += Number(o.quantity) || 1; sold[id].orders += 1;
  });

  // top rated from real customer reviews
  const productById = {};
  products.forEach((p) => { productById[String(p._id)] = p; productById[String(p.id)] = p; });
  const ratings = {};
  reviews.forEach((r) => {
    const n = Number(r.rating);
    const p = productById[String(r.productId)];
    if (!p || !(n > 0)) return;
    const k = String(p._id);
    ratings[k] = ratings[k] || { id: k, name: p.name, image: p.images?.[0], total: 0, count: 0 };
    ratings[k].total += n; ratings[k].count += 1;
  });

  return {
    monthSales, lastMonthSales,
    salesChange: lastMonthSales ? Math.round(((monthSales - lastMonthSales) / lastMonthSales) * 100) : null,
    monthOrders: thisMonth.length,
    todayOrders: orders.filter((o) => dayKey(o.orderDate) === today).length,
    avgOrder: sales.length ? sum(sales) / sales.length : 0,
    totalOrders: orders.length,
    byStatus,
    refundRequests: orders.filter((o) => o.refundStatus === "Requested").length,
    daily,
    stock, liveProducts: live.length, restock,
    mostOrdered: Object.values(sold).sort((a, b) => b.qty - a.qty || b.orders - a.orders).slice(0, 5),
    topRated: Object.values(ratings).map((r) => ({ ...r, avg: r.total / r.count }))
      .sort((a, b) => b.avg - a.avg || b.count - a.count).slice(0, 5),
    recentOrders: [...orders].sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate)).slice(0, 6),
  };
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const Stars = ({ value }) => (
  <span className="db-stars" title={`${value.toFixed(1)} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => <i key={i} className={value >= i - 0.25 ? "on" : value >= i - 0.75 ? "half" : ""}>★</i>)}
  </span>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState(null);
  const [approvals, setApprovals] = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [hoverDay, setHoverDay] = useState(null);
  const name = sessionStorage.getItem("adminName") || "Admin";

  useEffect(() => {
    const get = (url, opts) => fetch(`${backend_url}${url}`, opts).then((r) => r.json()).catch(() => null);
    Promise.all([
      get("/allorders"),
      get("/allproducts"),
      get("/getalluserscount"),
      get("/api/customerreview"),
      canAccess("approvals")
        ? get("/api/product-requests/count", { headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` } })
        : null,
    ]).then(([o, p, u, r, a]) => {
      setOrders(o?.orders || []);
      setProducts(Array.isArray(p) ? p : []);
      setUsers(u?.totalUsers ?? null);
      setReviews(Array.isArray(r) ? r : r?.reviews || []);
      setApprovals(a?.count || 0);
      setLoading(false);
    });
  }, []);

  const d = useMemo(() => computeDashboard({ orders, products, reviews }), [orders, products, reviews]);
  const chart = d.daily.slice(-days);
  const chartMax = Math.max(1, ...chart.map((x) => x.total));
  const chartTotal = chart.reduce((t, x) => t + x.total, 0);
  const chartOrders = chart.reduce((t, x) => t + x.orders, 0);
  const shown = hoverDay !== null ? chart[hoverDay] : null;

  const openOrders = (search) => navigate("/orders", search ? { state: { search } } : undefined);

  // "Needs your attention" — only things this user can act on
  const attention = [
    canAccess("orders") && { n: d.byStatus.Pending, label: "New orders to confirm", icon: "🛍️", tone: "amber", go: () => openOrders() },
    canAccess("orders") && { n: d.byStatus.Confirmed, label: "Ready to pack & ship", icon: "📦", tone: "indigo", go: () => openOrders() },
    canAccess("approvals") && { n: approvals, label: "Product changes to approve", icon: "✅", tone: "green", go: () => navigate("/product-approvals") },
    canAccess("stock") && { n: d.stock.out, label: "Products out of stock", icon: "⛔", tone: "red", go: () => navigate("/stock") },
    canAccess("stock") && { n: d.stock.low, label: "Products running low", icon: "⚠️", tone: "orange", go: () => navigate("/stock") },
    canAccess("orders") && { n: d.refundRequests, label: "Refund requests", icon: "↩️", tone: "pink", go: () => openOrders() },
  ].filter(Boolean);
  const pending = attention.filter((a) => a.n > 0);

  const quickActions = [
    canAccess("products") && { to: "/addproduct", label: "Add Product", icon: "＋", primary: true },
    canAccess("orders") && { to: "/orders", label: "Orders", icon: "🧾" },
    canAccess("stock") && { to: "/stock", label: "Update Stock", icon: "📦" },
    canAccess("excel-upload") && { to: "/excel-upload", label: "Excel Upload", icon: "📊" },
  ].filter(Boolean);

  const stockTotal = Math.max(1, d.liveProducts);
  const pct = (n, t) => Math.round((n / Math.max(1, t)) * 100);

  return (
    <div className="db-page">
      {/* ── Header ── */}
      <header className="db-hero">
        <div>
          <p className="db-date">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          <h1>{greeting()}, {name} 👋</h1>
          <p className="db-sub">Here's what's happening in your shop today.</p>
        </div>
        <div className="db-actions">
          {quickActions.map((a) => (
            <Link key={a.to} to={a.to} className={`db-action ${a.primary ? "primary" : ""}`}>
              <span>{a.icon}</span>{a.label}
            </Link>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="db-loading"><i className="db-spinner" /> Loading your dashboard…</div>
      ) : (
        <>
          {/* ── Needs attention ── */}
          <section className="db-section">
            <h2 className="db-h2">Needs your attention</h2>
            {pending.length === 0 ? (
              <div className="db-allclear">🎉 <b>All caught up!</b> No new orders, approvals or stock problems right now.</div>
            ) : (
              <div className="db-attention">
                {attention.map((a) => (
                  <button key={a.label} type="button" className={`db-alert tone-${a.tone} ${a.n ? "" : "is-zero"}`} onClick={a.go}>
                    <span className="db-alert-icon">{a.icon}</span>
                    <span className="db-alert-n">{a.n}</span>
                    <span className="db-alert-label">{a.label}</span>
                    <span className="db-alert-go">{a.n ? "Open →" : "✓ Done"}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* ── Key numbers ── */}
          <section className="db-kpis">
            <div className="db-kpi">
              <span className="db-kpi-label">Sales this month</span>
              <strong>{money(d.monthSales)}</strong>
              <span className={`db-kpi-trend ${d.salesChange === null ? "" : d.salesChange >= 0 ? "up" : "down"}`}>
                {d.salesChange === null ? `Last month: ${money(d.lastMonthSales)}` : `${d.salesChange >= 0 ? "▲" : "▼"} ${Math.abs(d.salesChange)}% vs last month`}
              </span>
            </div>
            <div className="db-kpi">
              <span className="db-kpi-label">Orders this month</span>
              <strong>{d.monthOrders}</strong>
              <span className="db-kpi-trend">{d.todayOrders} today · {d.totalOrders} in total</span>
            </div>
            <div className="db-kpi">
              <span className="db-kpi-label">Average order value</span>
              <strong>{money(d.avgOrder)}</strong>
              <span className="db-kpi-trend">Cancelled & refunded left out</span>
            </div>
            <div className="db-kpi">
              <span className="db-kpi-label">Customers</span>
              <strong>{users ?? "—"}</strong>
              <span className="db-kpi-trend">{d.liveProducts.toLocaleString("en-IN")} products live</span>
            </div>
          </section>

          {/* ── Sales chart + order status ── */}
          <section className="db-grid two">
            <div className="db-card">
              <div className="db-card-head">
                <div>
                  <h3>Sales</h3>
                  <p className="db-card-sub">
                    {shown
                      ? <>{shown.date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}: <b>{money(shown.total)}</b> · {shown.orders} order{shown.orders === 1 ? "" : "s"}</>
                      : <><b>{money(chartTotal)}</b> from {chartOrders} order{chartOrders === 1 ? "" : "s"} in the last {days} days</>}
                  </p>
                </div>
                <div className="db-seg" role="group" aria-label="Chart period">
                  {[7, 14, 30].map((n) => (
                    <button key={n} type="button" className={days === n ? "on" : ""} onClick={() => setDays(n)}>{n} days</button>
                  ))}
                </div>
              </div>
              <div className="db-chart" onMouseLeave={() => setHoverDay(null)}>
                {chart.map((x, i) => (
                  <div key={x.key} className={`db-bar-wrap ${i === chart.length - 1 ? "today" : ""} ${hoverDay === i ? "hover" : ""}`}
                    onMouseEnter={() => setHoverDay(i)}
                    title={`${x.date.toLocaleDateString("en-IN")}: ${money(x.total)} · ${x.orders} orders`}>
                    <div className="db-bar" style={{ height: `${x.total ? Math.max(6, (x.total / chartMax) * 100) : 2}%` }} />
                    {(days <= 14 || i % 3 === 0 || i === chart.length - 1) && (
                      <span className="db-bar-label">{i === chart.length - 1 ? "Today" : x.date.toLocaleDateString("en-IN", days <= 7 ? { weekday: "short" } : { day: "numeric" })}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="db-card">
              <div className="db-card-head">
                <div>
                  <h3>Orders by status</h3>
                  <p className="db-card-sub">{d.totalOrders} orders in total</p>
                </div>
                {canAccess("orders") && <Link to="/orders" className="db-link">View all →</Link>}
              </div>
              <div className="db-stack" aria-hidden="true">
                {STATUSES.map((s) => d.byStatus[s.key] > 0 && (
                  <span key={s.key} style={{ width: `${pct(d.byStatus[s.key], d.totalOrders)}%`, background: s.color }} />
                ))}
              </div>
              <ul className="db-status-list">
                {STATUSES.map((s) => (
                  <li key={s.key}>
                    <i style={{ background: s.color }} />
                    <span className="db-status-name">{s.label}<small>{s.hint}</small></span>
                    <b>{d.byStatus[s.key]}</b>
                    <span className="db-status-pct">{pct(d.byStatus[s.key], d.totalOrders)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ── Recent orders + stock health ── */}
          <section className="db-grid two">
            <div className="db-card">
              <div className="db-card-head">
                <h3>Recent orders</h3>
                {canAccess("orders") && <Link to="/orders" className="db-link">View all →</Link>}
              </div>
              {d.recentOrders.length === 0 ? <p className="db-empty">No orders yet.</p> : (
                <ul className="db-orders">
                  {d.recentOrders.map((o) => {
                    const s = STATUSES.find((x) => x.key === statusOf(o)) || STATUSES[0];
                    return (
                      <li key={o._id} onClick={() => canAccess("orders") && openOrders(o._id)} title="Open this order">
                        {o.productId?.images?.[0] ? <img src={o.productId.images[0]} alt="" /> : <span className="db-noimg">🛍️</span>}
                        <div className="db-order-main">
                          <span className="db-order-product">{o.productId?.name || "Product"}</span>
                          <span className="db-order-meta">{o.name} · {timeAgo(o.orderDate)}</span>
                        </div>
                        <span className="db-order-price">{money(o.totalPrice)}</span>
                        <span className="db-chip" style={{ color: s.color, background: `${s.color}1a` }}>{s.label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="db-card db-card-fill">
              <div className="db-card-head">
                <div>
                  <h3>Stock health</h3>
                  <p className="db-card-sub">{d.liveProducts.toLocaleString("en-IN")} live products</p>
                </div>
                {canAccess("stock") && <Link to="/stock" className="db-link">Manage stock →</Link>}
              </div>
              <div className="db-stock-tiles">
                <div className="in"><span className="db-tile-icon">✓</span><b>{d.stock.in.toLocaleString("en-IN")}</b><span>In stock</span><small>{pct(d.stock.in, stockTotal)}% of products</small></div>
                <div className="low"><span className="db-tile-icon">!</span><b>{d.stock.low.toLocaleString("en-IN")}</b><span>Running low</span><small>{pct(d.stock.low, stockTotal)}% of products</small></div>
                <div className="out"><span className="db-tile-icon">✕</span><b>{d.stock.out.toLocaleString("en-IN")}</b><span>Out of stock</span><small>{pct(d.stock.out, stockTotal)}% of products</small></div>
              </div>
              <div className="db-stock-bar" title={`${d.stock.in} in stock · ${d.stock.low} low · ${d.stock.out} out`}>
                <i className="in" style={{ width: `${pct(d.stock.in, stockTotal)}%` }} />
                <i className="low" style={{ width: `${pct(d.stock.low, stockTotal)}%` }} />
                <i className="out" style={{ width: `${pct(d.stock.out, stockTotal)}%` }} />
              </div>

              {/* fills the rest of the card, lined up with Recent orders */}
              {d.restock.length > 0 && (
                <div className="db-restock">
                  <span className="db-restock-title">Restock first</span>
                  <ul>
                    {d.restock.map((p) => (
                      <li key={p.id} onClick={() => canAccess("products") && navigate(`/editproduct/${p.id}`)}
                        title={canAccess("products") ? "Open this product" : ""}>
                        {p.image ? <img src={p.image} alt="" /> : <span className="db-noimg">🛍️</span>}
                        <span className="db-restock-name">{p.name}{p.code && <small>{p.code}</small>}</span>
                        <span className={`db-chip ${p.level === "out" ? "chip-out" : "chip-low"}`}>{p.level === "out" ? "Out" : "Low"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>

          {/* ── Best sellers + top rated ── */}
          <section className="db-grid two">
            <div className="db-card">
              <div className="db-card-head"><h3>🔥 Most ordered</h3></div>
              {d.mostOrdered.length === 0 ? <p className="db-empty">No orders yet.</p> : (
                <ul className="db-rank">
                  {d.mostOrdered.map((p, i) => (
                    <li key={p.id}>
                      <span className="db-rank-n">{i + 1}</span>
                      {p.image ? <img src={p.image} alt="" /> : <span className="db-noimg">🛍️</span>}
                      <span className="db-rank-name">{p.name}{p.code && <small>{p.code}</small>}</span>
                      <span className="db-chip neutral">{p.qty} sold</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="db-card">
              <div className="db-card-head"><h3>⭐ Top rated by customers</h3></div>
              {d.topRated.length === 0 ? <p className="db-empty">No customer ratings yet.</p> : (
                <ul className="db-rank">
                  {d.topRated.map((p, i) => (
                    <li key={p.id}>
                      <span className="db-rank-n">{i + 1}</span>
                      {p.image ? <img src={p.image} alt="" /> : <span className="db-noimg">🛍️</span>}
                      <span className="db-rank-name">{p.name}<small>{p.count} review{p.count === 1 ? "" : "s"}</small></span>
                      <Stars value={p.avg} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Dashboard;
